import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useEnhancedUserMetrics } from "@/hooks/useEnhancedUserMetrics";
import { useRuns } from "@/hooks/useRuns";
import { useAchievements } from "@/hooks/useAchievements";
import { useUserTrailPreferences } from "@/hooks/useTrailPreferences";
import Header from "@/components/Header";
import { MetricCard } from "@/components/MetricCard";
import { TrailCompletionDialog } from "@/components/TrailCompletionDialog";
import { RunsListDialog } from "@/components/RunsListDialog";
import { FavoriteTrailsDialog } from "@/components/FavoriteTrailsDialog";
import { WantToGoDialog } from "@/components/WantToGoDialog";
import { ProfileAchievements } from "@/components/ProfileAchievements";
import { ProfileActivity } from "@/components/ProfileActivity";
import { PremiumInsightsCard } from "@/components/PremiumInsightsCard";
import { Loader2 } from "lucide-react";
import {
  Mountain,
  Gauge,
  Calendar,
  Clock,
  TrendingUp,
  Camera,
  MapPin,
  Timer,
  Heart,
  Target,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/lib/routes";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { needsOnboarding, isLoading: isOnboardingLoading } = useOnboarding();
  const { data: metrics, isLoading } = useEnhancedUserMetrics(user?.id);
  const { runs } = useRuns(user?.id, 'start_time');
  const { earnedAchievements, availableAchievements, isLoading: isLoadingAchievements } = useAchievements(user?.id);
  const { favoritesCount, wantToGoCount, isLoading: isLoadingPreferences } = useUserTrailPreferences();

  const [trailDialogOpen, setTrailDialogOpen] = useState(false);
  const [runsDialogOpen, setRunsDialogOpen] = useState(false);
  const [favoritesDialogOpen, setFavoritesDialogOpen] = useState(false);
  const [wantToGoDialogOpen, setWantToGoDialogOpen] = useState(false);

  useEffect(() => {
    if (!isOnboardingLoading && needsOnboarding) {
      navigate(ROUTES.onboarding);
    }
  }, [needsOnboarding, isOnboardingLoading, navigate]);

  if (isOnboardingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const handleTrailsClick = () => {
    setTrailDialogOpen(true);
  };

  const handleSeeRuns = () => {
    setRunsDialogOpen(true);
  };

  const handleLogRun = () => {
    navigate(ROUTES.logRun);
  };

  const handleSeeLastRun = () => {
    if (metrics?.lastRun) {
      navigate(ROUTES.run(metrics.lastRun.id));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <MetricCard
            title="Trails Completed"
            value={metrics?.trailsCompletedCount || 0}
            icon={Mountain}
            gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
            subtitle={
              metrics?.trailsCompletedCount
                ? "≥80% of segments completed"
                : "Complete your first trail"
            }
            actions={
              metrics && metrics.trailsCompletedCount > 0
                ? [
                    {
                      label: "See Trails",
                      onClick: handleTrailsClick,
                      variant: "outline",
                    },
                  ]
                : undefined
            }
            isLoading={isLoading}
          />

          <MetricCard
            title="Miles Aired Down"
            value={metrics?.totalMiles || 0}
            icon={Gauge}
            gradient="bg-gradient-to-br from-blue-500 to-blue-600"
            subtitle="Total distance traveled"
            isLoading={isLoading}
          />

          <MetricCard
            title="Hours Aired Down"
            value={metrics?.totalHours || 0}
            icon={Timer}
            gradient="bg-gradient-to-br from-amber-500 to-amber-600"
            subtitle="Time spent on trails"
            isLoading={isLoading}
          />

          <MetricCard
            title="Runs Logged"
            value={metrics?.totalRuns || 0}
            icon={Calendar}
            gradient="bg-gradient-to-br from-purple-500 to-purple-600"
            subtitle={
              metrics?.totalRuns
                ? "Adventures tracked"
                : "Log your first run"
            }
            actions={[
              ...(metrics && metrics.totalRuns > 0
                ? [
                    {
                      label: "See Runs",
                      onClick: handleSeeRuns,
                      variant: "outline" as const,
                    },
                  ]
                : []),
              {
                label: "Log Run",
                onClick: handleLogRun,
                variant: "outline" as const,
              },
            ]}
            isLoading={isLoading}
          />

          <MetricCard
            title="Last Run"
            value={metrics?.lastRun?.relativeTime || "Never"}
            icon={Clock}
            gradient="bg-gradient-to-br from-orange-500 to-orange-600"
            subtitle={metrics?.lastRun?.title || "No runs logged yet"}
            actions={
              metrics?.lastRun
                ? [
                    {
                      label: "See Run",
                      onClick: handleSeeLastRun,
                      variant: "outline",
                    },
                  ]
                : undefined
            }
            isLoading={isLoading}
          />

          <MetricCard
            title="Active Months This Year"
            value={
              metrics
                ? `${metrics.activeMonthsThisYear.count}/${metrics.activeMonthsThisYear.total}`
                : "0/0"
            }
            icon={TrendingUp}
            gradient="bg-gradient-to-br from-pink-500 to-pink-600"
            subtitle={
              metrics?.activeMonthsThisYear.hasStreak
                ? `${metrics.activeMonthsThisYear.streakLength} month streak!`
                : "Keep the momentum going"
            }
            badge={
              metrics?.activeMonthsThisYear.hasStreak
                ? {
                    text: `🔥 ${metrics.activeMonthsThisYear.streakLength} month streak!`,
                    variant: "secondary",
                  }
                : undefined
            }
            isLoading={isLoading}
          />

          <MetricCard
            title="Photos Shared"
            value={metrics?.photosShared || 0}
            icon={Camera}
            gradient="bg-gradient-to-br from-indigo-500 to-indigo-600"
            subtitle={
              metrics?.photosShared
                ? "Memories captured"
                : "Share your adventures"
            }
            isLoading={isLoading}
          />

          <MetricCard
            title="Waypoints Shared"
            value={metrics?.waypointsShared || 0}
            icon={MapPin}
            gradient="bg-gradient-to-br from-teal-500 to-teal-600"
            subtitle={
              metrics?.waypointsShared
                ? "Points of interest marked"
                : "Mark your favorite spots"
            }
            isLoading={isLoading}
          />

          <MetricCard
            title="Views Received"
            value={metrics?.totalViewsReceived || 0}
            icon={Eye}
            gradient="bg-gradient-to-br from-slate-500 to-slate-600"
            subtitle={
              metrics?.totalViewsReceived
                ? "Unique browsers and members reached"
                : "Share a public run to get seen"
            }
            isLoading={isLoading}
          />

          <MetricCard
            title="Favorite Trails"
            value={favoritesCount}
            icon={Heart}
            gradient="bg-gradient-to-br from-red-500 to-red-600"
            subtitle={
              favoritesCount > 0
                ? "Trails you love"
                : "Save your favorite trails"
            }
            actions={
              favoritesCount > 0
                ? [
                    {
                      label: "View",
                      onClick: () => setFavoritesDialogOpen(true),
                      variant: "outline",
                    },
                  ]
                : undefined
            }
            isLoading={isLoadingPreferences}
          />

          <MetricCard
            title="Want to Go"
            value={wantToGoCount}
            icon={Target}
            gradient="bg-gradient-to-br from-cyan-500 to-cyan-600"
            subtitle={
              wantToGoCount > 0
                ? "Your trail wishlist"
                : "Build your adventure list"
            }
            actions={
              wantToGoCount > 0
                ? [
                    {
                      label: "View",
                      onClick: () => setWantToGoDialogOpen(true),
                      variant: "outline",
                    },
                  ]
                : undefined
            }
            isLoading={isLoadingPreferences}
          />
        </div>

        <div className="mb-8">
          <PremiumInsightsCard
            totalMiles={metrics?.totalMiles}
            totalRuns={metrics?.totalRuns}
            activeMonths={metrics?.activeMonthsThisYear?.count}
            trailsCompleted={metrics?.trailsCompletedCount}
            isLoading={isLoading}
          />
        </div>

        <ProfileAchievements
          earnedAchievements={earnedAchievements}
          availableAchievements={availableAchievements}
          isLoading={isLoadingAchievements}
          userMetrics={metrics}
        />

        <div className="mt-8">
          <ProfileActivity
            recentRuns={runs?.slice(0, 5)}
            isLoading={isLoading}
            onSeeRuns={handleSeeRuns}
          />
        </div>
      </div>

      <TrailCompletionDialog
        open={trailDialogOpen}
        onOpenChange={setTrailDialogOpen}
        trails={metrics?.trailsCompleted || []}
      />
      <RunsListDialog
        open={runsDialogOpen}
        onOpenChange={setRunsDialogOpen}
        runs={runs}
      />
      <FavoriteTrailsDialog
        open={favoritesDialogOpen}
        onOpenChange={setFavoritesDialogOpen}
      />
      <WantToGoDialog
        open={wantToGoDialogOpen}
        onOpenChange={setWantToGoDialogOpen}
      />
    </div>
  );
};

export default Dashboard;
